import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import {
  completePromoIngest,
  getPromoIngest,
  getPromoPayload,
  startPromoIngest,
  uploadPromoMp4,
} from '../../services/promoSurfacesApi';
import { ingestStatusLabel } from './promoSurfacesUtils';
import { usePromoSurfacesSession } from './usePromoSurfacesSession';

const POLL_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function PromoIngestPanel({ surfaceKey, template, disabled, onReload, onNotice }) {
  const ingest = template?.ingest || null;
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const { handleApiResponse } = usePromoSurfacesSession(setError);
  const onNoticeRef = useRef(onNotice);
  const onReloadRef = useRef(onReload);
  onNoticeRef.current = onNotice;
  onReloadRef.current = onReload;

  useEffect(() => {
    const ingestId = template?.ingest?.ingest_id;
    const status = template?.ingest?.status;
    if (!ingestId || status !== 'processing') return undefined;
    let stopped = false;
    const watch = async () => {
      while (!stopped) {
        const result = await getPromoIngest(ingestId);
        if (stopped) return;
        if (handleApiResponse(result, 'Could not check the conversion.')) return;
        const data = getPromoPayload(result);
        if (data?.status === 'ready') {
          onNoticeRef.current('Video is ready.');
          await onReloadRef.current();
          return;
        }
        if (data?.status === 'failed') {
          setError(data.error_message || 'Video conversion failed.');
          await onReloadRef.current();
          return;
        }
        await sleep(POLL_MS);
      }
    };
    watch();
    return () => {
      stopped = true;
    };
  }, [handleApiResponse, template?.ingest?.ingest_id, template?.ingest?.status]);

  const handleUpload = async () => {
    if (!file || !template?.id) return;
    const isMp4 = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');
    if (!isMp4) {
      setError('Choose an MP4 file.');
      return;
    }
    setBusy('starting');
    setError('');
    try {
      const started = await startPromoIngest(surfaceKey, template.id, {
        file_name: file.name || 'promo.mp4',
        content_type: 'video/mp4',
      });
      if (handleApiResponse(started, 'Could not start the upload.')) return;
      const startData = getPromoPayload(started);
      if (!startData?.upload_url || !startData?.ingest_id) {
        setError('The upload link was missing from the response.');
        return;
      }
      setBusy('uploading');
      await uploadPromoMp4(startData.upload_url, file);
      setBusy('completing');
      const completed = await completePromoIngest(startData.ingest_id);
      if (handleApiResponse(completed, 'The file is not in storage yet. Try the upload again.')) return;
      const completedData = getPromoPayload(completed);
      setFile(null);
      onNotice(
        completedData?.status === 'ready'
          ? 'Video is ready.'
          : 'Upload received. Conversion is running. A slot that was showing disappears until the video is ready.'
      );
      await onReload();
    } catch (uploadError) {
      setError(uploadError.message || 'The upload failed.');
    } finally {
      setBusy('');
    }
  };

  const status = ingest?.status || null;
  const busyLabel =
    busy === 'starting' ? 'Preparing the upload…' : busy === 'uploading' ? 'Uploading the MP4…' : busy === 'completing' ? 'Starting conversion…' : '';

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1">MP4 upload</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The file goes to storage, then conversion writes the HLS playlist onto this template. Starting an upload clears
        the current video. A slot that was showing disappears until conversion is ready. If this template was created
        to become active, it does so when the video is ready and the creative is eligible. The converter does not
        create a poster.
      </Typography>
      {status && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Conversion: {ingestStatusLabel(status)}
          {ingest?.error_message ? ` — ${ingest.error_message}` : ''}
        </Typography>
      )}
      {template?.media?.m3u8_url && (
        <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>
          Playlist: {template.media.m3u8_url}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {busyLabel && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {busyLabel}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="file"
          accept="video/mp4,.mp4"
          aria-label="MP4 file"
          disabled={Boolean(busy) || disabled}
          onChange={(event) => setFile(event.target.files?.[0] || null)}
        />
        <Button variant="contained" onClick={handleUpload} disabled={!file || Boolean(busy) || disabled}>
          Upload MP4
        </Button>
      </Box>
      {disabled && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Save changes before uploading.
        </Typography>
      )}
    </Box>
  );
}

export default PromoIngestPanel;
